import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { orderService } from '../services/orderService';
import { paymentService } from '../services/paymentService';
import { getProductPrice, getCommission, getDeliveryFee, formatPrice } from '../utils/priceHelpers';
import ConfirmDialog from '../components/ConfirmDialog';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const AgentDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const toast = useToast();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, onConfirm: null, title: '', message: '', type: 'warning' });
  const [priceForm, setPriceForm] = useState({
    items: [], // Pour les commandes avec plusieurs produits
    productPrice: '', // Pour l'ancien format (un seul produit)
    weight: '',
    commissionRate: '0.15',
    deliveryFee: '',
    totalWeight: '', // Pour les commandes avec plusieurs produits
  });
  const [calculating, setCalculating] = useState(false);
  const [calculation, setCalculation] = useState(null);
  const [shippingOrder, setShippingOrder] = useState(null);
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [shipping, setShipping] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [statusOrder, setStatusOrder] = useState(null);
  const [newDeliveryStatus, setNewDeliveryStatus] = useState('');
  
  // Filtres et recherche
  const [filters, setFilters] = useState({
    status: '',
    paymentStatus: '',
    deliveryStatus: '',
    search: '',
  });
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  useEffect(() => {
    if (!user || (user.role !== 'agent' && user.role !== 'admin')) {
      navigate('/dashboard');
      return;
    }
    fetchOrders();
  }, [user, filters, pagination.page]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await orderService.getAllOrders({
        ...filters,
        page: pagination.page,
        limit: pagination.limit,
      });
      if (response.success) {
        setOrders(response.orders || []);
        setStats(response.stats || null);
        setPagination(prev => ({
          ...prev,
          total: response.total || 0,
          totalPages: response.totalPages || 0,
        }));
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des commandes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value,
    }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset à la page 1 lors d'un changement de filtre
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setFilters(prev => ({
      ...prev,
      search: value,
    }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      paymentStatus: '',
      deliveryStatus: '',
      search: '',
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePriceChange = (e) => {
    const { name, value } = e.target;
    setPriceForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Calcul automatique si le prix du produit est saisi (ancien format)
    if (name === 'productPrice' && value && !selectedOrder?.items) {
      calculatePrice(value, priceForm.weight, priceForm.commissionRate, priceForm.deliveryFee);
    }
  };

  const handleItemPriceChange = (index, field, value) => {
    const newItems = [...priceForm.items];
    newItems[index] = {
      ...newItems[index],
      [field]: value,
    };
    setPriceForm(prev => ({
      ...prev,
      items: newItems,
    }));
    calculateMultiProductPrice(newItems, priceForm.commissionRate, priceForm.deliveryFee, priceForm.totalWeight);
  };

  const calculatePrice = (productPrice, weight, commissionRate, deliveryFee) => {
    if (!productPrice || productPrice <= 0) {
      setCalculation(null);
      return;
    }

    const price = parseFloat(productPrice);
    const rate = parseFloat(commissionRate) || 0.15;
    const commission = price * rate;
    const delivery = deliveryFee ? parseFloat(deliveryFee) : (weight ? parseFloat(weight) * 2 + 35 : 35);
    const total = price + commission + delivery;

    setCalculation({
      productPrice: price,
      commission,
      commissionRate: rate,
      deliveryFee: delivery,
      totalAmount: total,
    });
  };

  const calculateMultiProductPrice = (items, commissionRate, deliveryFee, totalWeight) => {
    if (!items || items.length === 0) {
      setCalculation(null);
      return;
    }

    const rate = parseFloat(commissionRate) || 0.15;
    let totalProductPrice = 0;
    let totalCommission = 0;

    // Calculer pour chaque produit
    items.forEach((item) => {
      const price = parseFloat(item.productPrice) || 0;
      if (price > 0) {
        const commission = price * rate;
        totalProductPrice += price;
        totalCommission += commission;
      }
    });

    // Calculer les frais de livraison
    const delivery = deliveryFee ? parseFloat(deliveryFee) : (totalWeight ? parseFloat(totalWeight) * 2 + 35 : 35);
    const totalAmount = totalProductPrice + totalCommission + delivery;

    setCalculation({
      totalProductPrice,
      totalCommission,
      commissionRate: rate,
      totalDeliveryFee: delivery,
      totalAmount,
      items: items.map(item => ({
        productPrice: parseFloat(item.productPrice) || 0,
        commission: (parseFloat(item.productPrice) || 0) * rate,
        subtotal: (parseFloat(item.productPrice) || 0) * (1 + rate),
      })),
    });
  };

  const handleSubmitPrice = async (e) => {
    e.preventDefault();
    if (!selectedOrder) return;

    setCalculating(true);
    try {
      // Vérifier si c'est une commande avec plusieurs produits
      const hasMultipleProducts = selectedOrder.items && Array.isArray(selectedOrder.items) && selectedOrder.items.length > 0;

      let priceData;
      if (hasMultipleProducts) {
        // Nouveau format : plusieurs produits
        const items = priceForm.items.map(item => ({
          productPrice: parseFloat(item.productPrice) || 0,
        }));

        // Vérifier que tous les produits ont un prix
        if (items.some(item => item.productPrice <= 0)) {
          toast.warning('Veuillez définir un prix pour tous les produits');
          setCalculating(false);
          return;
        }

        priceData = {
          items,
          commissionRate: priceForm.commissionRate ? parseFloat(priceForm.commissionRate) : undefined,
          deliveryFee: priceForm.deliveryFee ? parseFloat(priceForm.deliveryFee) : undefined,
          totalWeight: priceForm.totalWeight ? parseFloat(priceForm.totalWeight) : undefined,
        };
      } else {
        // Ancien format : un seul produit
        priceData = {
          productPrice: parseFloat(priceForm.productPrice),
          weight: priceForm.weight ? parseFloat(priceForm.weight) : undefined,
          commissionRate: priceForm.commissionRate ? parseFloat(priceForm.commissionRate) : undefined,
          deliveryFee: priceForm.deliveryFee ? parseFloat(priceForm.deliveryFee) : undefined,
        };
      }

      const response = await orderService.updateOrderPrice(selectedOrder._id, priceData);

      if (response.success) {
        toast.success('Prix mis à jour avec succès !');
        setSelectedOrder(null);
        setPriceForm({ items: [], productPrice: '', weight: '', commissionRate: '0.15', deliveryFee: '', totalWeight: '' });
        setCalculation(null);
        fetchOrders();
      } else {
        toast.error(`Erreur: ${response.error}`);
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Une erreur est survenue');
    } finally {
      setCalculating(false);
    }
  };

  const openPriceModal = (order) => {
    setSelectedOrder(order);
    
    // Vérifier si c'est une commande avec plusieurs produits
    const hasMultipleProducts = order.items && Array.isArray(order.items) && order.items.length > 0;

    if (hasMultipleProducts) {
      // Nouveau format : plusieurs produits
      const items = order.items.map(item => ({
        productPrice: item.productPrice || item.verifiedPrice || '',
      }));
      setPriceForm({
        items,
        productPrice: '',
        weight: '',
        commissionRate: '0.15',
        deliveryFee: order.totalDeliveryFee || '',
        totalWeight: '',
      });
      if (items.some(item => item.productPrice)) {
        calculateMultiProductPrice(items, '0.15', order.totalDeliveryFee || '', '');
      }
    } else {
      // Ancien format : un seul produit
      setPriceForm({
        items: [],
        productPrice: order.productPrice || order.verifiedPrice || '',
        weight: '',
        commissionRate: '0.15',
        deliveryFee: order.deliveryFee || '',
        totalWeight: '',
      });
      if (order.productPrice || order.verifiedPrice) {
        calculatePrice(
          order.productPrice || order.verifiedPrice,
          '',
          '0.15',
          order.deliveryFee || ''
        );
      }
    }
  };

  const handleShipOrder = async () => {
    if (!shippingOrder) return;

    setShipping(true);
    try {
      const response = await orderService.shipOrder(shippingOrder._id, deliveryAddress);
      if (response.success) {
        toast.success(`Commande expédiée avec succès ! Tracking ID: ${response.trackingId}`);
        setShippingOrder(null);
        setDeliveryAddress('');
        fetchOrders();
      } else {
        toast.error(`Erreur: ${response.error}`);
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Une erreur est survenue');
    } finally {
      setShipping(false);
    }
  };

  const handleUpdateDeliveryStatus = async () => {
    if (!statusOrder || !newDeliveryStatus) return;

    setUpdatingStatus(true);
    try {
      const response = await orderService.updateDeliveryStatus(statusOrder._id, newDeliveryStatus);
      if (response.success) {
        toast.success('Statut de livraison mis à jour avec succès !');
        setStatusOrder(null);
        setNewDeliveryStatus('');
        fetchOrders();
      } else {
        toast.error(`Erreur: ${response.error}`);
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Une erreur est survenue');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const openShipModal = (order) => {
    setShippingOrder(order);
    setDeliveryAddress(order.deliveryAddress || '');
  };

  const openStatusModal = (order) => {
    setStatusOrder(order);
    setNewDeliveryStatus(order.deliveryStatus || 'Non expédié');
  };

  const handleSimulatePayment = async (orderId) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Simuler un paiement',
      message: 'Voulez-vous simuler un paiement réussi pour cette commande ?\n\nCette action va :\n- Marquer la commande comme payée\n- Créer une notification pour le client\n- Permettre de tester le processus complet',
      type: 'info',
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, isOpen: false });
        try {
          const response = await paymentService.simulatePayment(orderId);
          if (response.success) {
            toast.success('✅ Paiement simulé avec succès ! La commande est maintenant marquée comme payée. Le client recevra une notification.');
            fetchOrders();
          } else {
            toast.error(`Erreur: ${response.error}`);
          }
        } catch (error) {
          console.error('Erreur:', error);
          toast.error('Une erreur est survenue lors de la simulation du paiement');
        }
      },
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'Commandée': '#FF0000',
      'Payée': '#28a745',
      'Expédiée': '#ffc107',
      'Livrée': '#28a745',
      'Annulée': '#dc3545',
    };
    return colors[status] || '#6c757d';
  };

  if (loading) {
    return (
      <div className="container">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        onConfirm={() => {
          if (confirmDialog.onConfirm) {
            confirmDialog.onConfirm();
          }
          setConfirmDialog({ ...confirmDialog, isOpen: false });
        }}
        onCancel={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
      />

      <div className="container">
        <div style={{ margin: '40px auto', maxWidth: '1400px' }}>
          <h1 style={{ marginBottom: '30px', color: '#FF0000', fontSize: '32px', fontWeight: '700' }}>
            📊 Dashboard Agent
          </h1>

          {/* Statistiques */}
          {stats && (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '15px', 
              marginBottom: '30px' 
            }}>
              <div className="card" style={{ textAlign: 'center', padding: '20px', backgroundColor: '#f8f9fa' }}>
                <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#FF0000' }}>{stats.total}</div>
                <div style={{ color: '#666', fontSize: '14px' }}>Total commandes</div>
              </div>
              <div className="card" style={{ textAlign: 'center', padding: '20px', backgroundColor: '#fff3cd' }}>
                <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#856404' }}>{stats.commandee}</div>
                <div style={{ color: '#666', fontSize: '14px' }}>Commandées</div>
              </div>
              <div className="card" style={{ textAlign: 'center', padding: '20px', backgroundColor: '#d1ecf1' }}>
                <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#0c5460' }}>{stats.payee}</div>
                <div style={{ color: '#666', fontSize: '14px' }}>Payées</div>
              </div>
              <div className="card" style={{ textAlign: 'center', padding: '20px', backgroundColor: '#fff3cd' }}>
                <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#856404' }}>{stats.expediee}</div>
                <div style={{ color: '#666', fontSize: '14px' }}>Expédiées</div>
              </div>
              <div className="card" style={{ textAlign: 'center', padding: '20px', backgroundColor: '#d4edda' }}>
                <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#155724' }}>{stats.livree}</div>
                <div style={{ color: '#666', fontSize: '14px' }}>Livrées</div>
              </div>
              <div className="card" style={{ textAlign: 'center', padding: '20px', backgroundColor: '#f8d7da' }}>
                <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#721c24' }}>{stats.enAttentePaiement}</div>
                <div style={{ color: '#666', fontSize: '14px' }}>En attente paiement</div>
              </div>
            </div>
          )}

          {/* Filtres et recherche */}
          <div className="card" style={{ marginBottom: '20px', padding: '20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '15px' }}>
              {/* Recherche */}
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>
                  🔍 Recherche
                </label>
                <input
                  type="text"
                  placeholder="ID, Tracking ID, produit..."
                  value={filters.search}
                  onChange={handleSearch}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '14px',
                  }}
                />
              </div>

              {/* Filtre statut */}
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>
                  📝 Statut
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '14px',
                  }}
                >
                  <option value="">Tous</option>
                  <option value="Commandée">Commandée</option>
                  <option value="Payée">Payée</option>
                  <option value="Expédiée">Expédiée</option>
                  <option value="Livrée">Livrée</option>
                  <option value="Annulée">Annulée</option>
                </select>
              </div>

              {/* Filtre paiement */}
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>
                  💳 Paiement
                </label>
                <select
                  value={filters.paymentStatus}
                  onChange={(e) => handleFilterChange('paymentStatus', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '14px',
                  }}
                >
                  <option value="">Tous</option>
                  <option value="En attente">En attente</option>
                  <option value="À la livraison">À la livraison</option>
                  <option value="Payé">Payé</option>
                  <option value="Échoué">Échoué</option>
                  <option value="Remboursé">Remboursé</option>
                </select>
              </div>

              {/* Filtre livraison */}
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>
                  🚚 Livraison
                </label>
                <select
                  value={filters.deliveryStatus}
                  onChange={(e) => handleFilterChange('deliveryStatus', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '14px',
                  }}
                >
                  <option value="">Tous</option>
                  <option value="Non expédié">Non expédié</option>
                  <option value="En transit">En transit</option>
                  <option value="Arrivé en RDC">Arrivé en RDC</option>
                  <option value="Prêt à être livré">Prêt à être livré</option>
                  <option value="Livré">Livré</option>
                </select>
              </div>
            </div>

            {/* Boutons d'action */}
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button
                onClick={clearFilters}
                className="btn btn-secondary"
                style={{ fontSize: '14px' }}
              >
                🔄 Réinitialiser les filtres
              </button>
              <button
                onClick={fetchOrders}
                className="btn btn-secondary"
                style={{ fontSize: '14px' }}
              >
                🔄 Actualiser
              </button>
              <div style={{ flex: 1, textAlign: 'right', paddingTop: '8px' }}>
                <span style={{ color: '#666', fontSize: '14px' }}>
                  Affichage: <strong>{orders.length}</strong> sur <strong>{pagination.total}</strong> commande(s)
                </span>
              </div>
            </div>
          </div>

          {/* Liste des commandes */}
          {loading ? (
            <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
              <p>Chargement...</p>
            </div>
          ) : orders.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
              <p style={{ fontSize: '18px', color: '#666' }}>
                {filters.status || filters.paymentStatus || filters.deliveryStatus || filters.search
                  ? 'Aucune commande ne correspond aux filtres sélectionnés'
                  : 'Aucune commande pour le moment'}
              </p>
              {(filters.status || filters.paymentStatus || filters.deliveryStatus || filters.search) && (
                <button
                  onClick={clearFilters}
                  className="btn btn-primary"
                  style={{ marginTop: '15px' }}
                >
                  Réinitialiser les filtres
                </button>
              )}
            </div>
          ) : (
            <>
              <div style={{ display: 'grid', gap: '20px' }}>
                {orders.map((order) => (
                <div key={order._id} className="card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '15px' }}>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ margin: '0 0 10px 0', color: '#FF0000' }}>
                        Commande #{order._id.slice(-8)}
                        {order.items && Array.isArray(order.items) && order.items.length > 0 && (
                          <span style={{ fontSize: '14px', color: '#666', fontWeight: 'normal', marginLeft: '10px' }}>
                            ({order.items.length} produit{order.items.length > 1 ? 's' : ''})
                          </span>
                        )}
                      </h3>
                      <p style={{ margin: '5px 0', fontSize: '14px' }}>
                        <strong>Plateforme:</strong> {order.platform}
                      </p>
                      
                      {/* Affichage des produits */}
                      {order.items && Array.isArray(order.items) && order.items.length > 0 ? (
                        // Nouveau format : plusieurs produits
                        <div style={{ marginTop: '10px', marginBottom: '10px' }}>
                          {order.items.map((item, index) => (
                            <div key={index} style={{ 
                              marginBottom: '10px', 
                              padding: '10px', 
                              backgroundColor: '#f8f9fa', 
                              borderRadius: '8px',
                              border: '1px solid #e8e8e8'
                            }}>
                              <p style={{ margin: '3px 0', fontSize: '14px', fontWeight: 'bold' }}>
                                Produit {index + 1}
                                {item.verifiedProductName && (
                                  <span style={{ fontWeight: 'normal', color: '#666', marginLeft: '8px' }}>
                                    - {item.verifiedProductName}
                                  </span>
                                )}
                              </p>
                              <p style={{ margin: '3px 0', fontSize: '13px' }}>
                                <strong>Lien:</strong>{' '}
                                <a href={item.productLink} target="_blank" rel="noopener noreferrer" style={{ color: '#FF0000', fontSize: '12px' }}>
                                  Voir le produit
                                </a>
                              </p>
                              <p style={{ margin: '3px 0', fontSize: '13px' }}>
                                <strong>Quantité:</strong> {item.quantity}
                                {item.size && <span> | <strong>Taille:</strong> {item.size}</span>}
                                {item.color && <span> | <strong>Couleur:</strong> {item.color}</span>}
                              </p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        // Ancien format : un seul produit
                        <>
                          <p style={{ margin: '5px 0', fontSize: '14px' }}>
                            <strong>Lien:</strong>{' '}
                            <a href={order.productLink} target="_blank" rel="noopener noreferrer" style={{ color: '#FF0000' }}>
                              Voir le produit
                            </a>
                          </p>
                          {order.verifiedProductName && (
                            <p style={{ margin: '5px 0', fontSize: '14px' }}>
                              <strong>Produit:</strong> {order.verifiedProductName}
                            </p>
                          )}
                          <p style={{ margin: '5px 0', fontSize: '14px' }}>
                            <strong>Quantité:</strong> {order.quantity}
                          </p>
                        </>
                      )}
                      <p style={{ margin: '5px 0', fontSize: '14px' }}>
                        <strong>Statut:</strong>{' '}
                        <span style={{ color: getStatusColor(order.status), fontWeight: 'bold' }}>
                          {order.status}
                        </span>
                      </p>
                      <p style={{ margin: '5px 0', fontSize: '14px' }}>
                        <strong>Paiement:</strong>{' '}
                        <span
                          style={{
                            color:
                              order.paymentStatus === 'Payé'
                                ? '#28a745'
                                : order.paymentStatus === 'À la livraison'
                                  ? '#f57f17'
                                  : '#666',
                          }}
                        >
                          {order.paymentStatus}
                          {order.paymentMethod === 'cash_on_delivery' ? ' (COD)' : ''}
                        </span>
                      </p>
                      <p style={{ margin: '5px 0', fontSize: '14px' }}>
                        <strong>Livraison:</strong>{' '}
                        <span style={{ color: '#666' }}>
                          {order.deliveryStatus}
                        </span>
                      </p>
                      {order.trackingId && (
                        <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                          <p style={{ margin: '0', fontSize: '14px', fontWeight: 'bold', color: '#0369a1' }}>
                            🏷️ Tracking: <span style={{ fontFamily: 'monospace' }}>{order.trackingId}</span>
                          </p>
                        </div>
                      )}
                      {order.totalAmount && (
                        <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f0f9ff', borderRadius: '8px' }}>
                          {(() => {
                            const hasMultipleProducts = order.items && Array.isArray(order.items) && order.items.length > 0;
                            const productPrice = getProductPrice(order);
                            const commission = getCommission(order);
                            const deliveryFee = getDeliveryFee(order);
                            
                            return (
                              <>
                                <p style={{ margin: '5px 0', fontSize: '14px' }}>
                                  <strong>Prix {hasMultipleProducts ? 'total produits' : 'produit'}:</strong> ${formatPrice(productPrice)}
                                </p>
                                <p style={{ margin: '5px 0', fontSize: '14px' }}>
                                  <strong>Commission{hasMultipleProducts ? ' totale' : ''}:</strong> ${formatPrice(commission)}
                                </p>
                                <p style={{ margin: '5px 0', fontSize: '14px' }}>
                                  <strong>Frais livraison:</strong> ${formatPrice(deliveryFee)}
                                </p>
                                <p style={{ margin: '5px 0', fontSize: '16px', fontWeight: 'bold', color: '#FF0000' }}>
                                  <strong>Total:</strong> ${formatPrice(order.totalAmount)}
                                </p>
                              </>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
                      {!order.totalAmount && (
                        <button
                          onClick={() => openPriceModal(order)}
                          className="btn btn-primary"
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          💰 Définir le prix
                        </button>
                      )}
                      {order.totalAmount && order.paymentStatus !== 'Payé' && order.paymentStatus !== 'Remboursé' && (
                        <button
                          onClick={() => handleSimulatePayment(order._id)}
                          className="btn btn-primary"
                          style={{ whiteSpace: 'nowrap', backgroundColor: '#17a2b8' }}
                          title={
                            order.paymentMethod === 'cash_on_delivery'
                              ? 'Confirmer que le paiement a été reçu à la livraison'
                              : 'Simuler un paiement réussi pour tester le processus'
                          }
                        >
                          {order.paymentMethod === 'cash_on_delivery'
                            ? '💵 Confirmer paiement reçu'
                            : '✅ Simuler paiement'}
                        </button>
                      )}
                      {((order.paymentStatus === 'Payé') ||
                        (order.paymentMethod === 'cash_on_delivery' &&
                          order.paymentStatus === 'À la livraison')) &&
                        !order.trackingId && (
                        <button
                          onClick={() => openShipModal(order)}
                          className="btn btn-primary"
                          style={{ whiteSpace: 'nowrap', backgroundColor: '#28a745' }}
                        >
                          🚚 Expédier
                        </button>
                      )}
                      {order.trackingId && (
                        <button
                          onClick={() => openStatusModal(order)}
                          className="btn btn-secondary"
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          📦 Mettre à jour statut
                        </button>
                      )}
                      <button
                        onClick={() => navigate(`/tracking/${order._id}`)}
                        className="btn btn-secondary"
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        👁️ Voir suivi
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  gap: '10px', 
                  marginTop: '30px',
                  flexWrap: 'wrap'
                }}>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page === 1}
                    className="btn btn-secondary"
                    style={{ 
                      opacity: pagination.page === 1 ? 0.5 : 1,
                      cursor: pagination.page === 1 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    ← Précédent
                  </button>
                  
                  <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                    <span style={{ color: '#666', fontSize: '14px' }}>
                      Page <strong>{pagination.page}</strong> sur <strong>{pagination.totalPages}</strong>
                    </span>
                  </div>

                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page >= pagination.totalPages}
                    className="btn btn-secondary"
                    style={{ 
                      opacity: pagination.page >= pagination.totalPages ? 0.5 : 1,
                      cursor: pagination.page >= pagination.totalPages ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Suivant →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modal pour expédier une commande */}
      {shippingOrder && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px',
          }}
          onClick={() => {
            setShippingOrder(null);
            setDeliveryAddress('');
          }}
        >
          <div
            className="card"
            style={{
              maxWidth: '500px',
              width: '100%',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginBottom: '20px', color: '#FF0000' }}>
              🚚 Expédier la commande #{shippingOrder._id.slice(-8)}
            </h2>
            <p style={{ color: '#666', marginBottom: '20px' }}>
              Un tracking ID sera généré automatiquement pour cette commande.
            </p>
            <div className="form-group">
              <label htmlFor="deliveryAddress">Adresse de livraison (optionnel)</label>
              <textarea
                id="deliveryAddress"
                value={deliveryAddress}
                onChange={(e) => setDeliveryAddress(e.target.value)}
                placeholder="Adresse complète du client"
                rows="3"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button
                className="btn btn-primary"
                onClick={handleShipOrder}
                disabled={shipping}
                style={{ backgroundColor: '#28a745' }}
              >
                {shipping ? 'Expédition...' : '🚚 Expédier la commande'}
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShippingOrder(null);
                  setDeliveryAddress('');
                }}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal pour mettre à jour le statut de livraison */}
      {statusOrder && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px',
          }}
          onClick={() => {
            setStatusOrder(null);
            setNewDeliveryStatus('');
          }}
        >
          <div
            className="card"
            style={{
              maxWidth: '500px',
              width: '100%',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginBottom: '20px', color: '#FF0000' }}>
              📦 Mettre à jour le statut de livraison
            </h2>
            <p style={{ color: '#666', marginBottom: '10px' }}>
              Commande #{statusOrder._id.slice(-8)}
            </p>
            <p style={{ color: '#666', marginBottom: '20px' }}>
              Statut actuel: <strong>{statusOrder.deliveryStatus}</strong>
            </p>
            <div className="form-group">
              <label htmlFor="deliveryStatus">Nouveau statut</label>
              <select
                id="deliveryStatus"
                value={newDeliveryStatus}
                onChange={(e) => setNewDeliveryStatus(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '14px',
                }}
              >
                <option value="Non expédié">Non expédié</option>
                <option value="En transit">En transit</option>
                <option value="Arrivé en RDC">Arrivé en RDC</option>
                <option value="Prêt à être livré">Prêt à être livré</option>
                <option value="Livré">Livré</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button
                className="btn btn-primary"
                onClick={handleUpdateDeliveryStatus}
                disabled={updatingStatus || !newDeliveryStatus}
              >
                {updatingStatus ? 'Mise à jour...' : 'Mettre à jour'}
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setStatusOrder(null);
                  setNewDeliveryStatus('');
                }}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal pour définir le prix */}
      {selectedOrder && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px',
          }}
          onClick={() => setSelectedOrder(null)}
        >
          <div
            className="card"
            style={{
              maxWidth: '500px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginBottom: '20px', color: '#FF0000' }}>
              💰 Définir le prix - Commande #{selectedOrder._id.slice(-8)}
            </h2>

            <form onSubmit={handleSubmitPrice}>
              {/* Vérifier si c'est une commande avec plusieurs produits */}
              {selectedOrder?.items && Array.isArray(selectedOrder.items) && selectedOrder.items.length > 0 ? (
                // Nouveau format : plusieurs produits
                <>
                  <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                    <p style={{ margin: 0, color: '#0369a1', fontSize: '14px', fontWeight: 'bold' }}>
                      💡 Cette commande contient {selectedOrder.items.length} produit{selectedOrder.items.length > 1 ? 's' : ''}. Définissez le prix pour chacun.
                    </p>
                  </div>

                  {priceForm.items.map((item, index) => (
                    <div key={index} style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e8e8e8' }}>
                      <h4 style={{ marginBottom: '15px', color: '#333', fontSize: '16px' }}>
                        Produit {index + 1}
                        {selectedOrder.items[index].verifiedProductName && (
                          <span style={{ fontSize: '14px', color: '#666', fontWeight: 'normal', marginLeft: '8px' }}>
                            - {selectedOrder.items[index].verifiedProductName}
                          </span>
                        )}
                      </h4>
                      <div className="form-group">
                        <label htmlFor={`productPrice-${index}`}>
                          Prix du produit (USD) *
                          {selectedOrder.items[index].verifiedPrice && (
                            <span style={{ fontSize: '12px', color: '#666', fontWeight: 'normal', marginLeft: '5px' }}>
                              (Prix vérifié: ${selectedOrder.items[index].verifiedPrice.toFixed(2)})
                            </span>
                          )}
                        </label>
                        <input
                          type="number"
                          id={`productPrice-${index}`}
                          value={item.productPrice}
                          onChange={(e) => handleItemPriceChange(index, 'productPrice', e.target.value)}
                          step="0.01"
                          min="0"
                          required
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  ))}

                  <div className="form-group">
                    <label htmlFor="totalWeight">Poids total estimé (kg) - Optionnel</label>
                    <input
                      type="number"
                      id="totalWeight"
                      name="totalWeight"
                      value={priceForm.totalWeight}
                      onChange={handlePriceChange}
                      step="0.1"
                      min="0"
                      placeholder="0.0"
                    />
                    <small style={{ color: '#666', fontSize: '12px' }}>
                      Si non renseigné, frais de livraison par défaut: $35
                    </small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="commissionRate">Taux de commission (0-1) *</label>
                    <input
                      type="number"
                      id="commissionRate"
                      name="commissionRate"
                      value={priceForm.commissionRate}
                      onChange={handlePriceChange}
                      step="0.01"
                      min="0"
                      max="1"
                      placeholder="0.15"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="deliveryFee">Frais de livraison (USD) - Optionnel</label>
                    <input
                      type="number"
                      id="deliveryFee"
                      name="deliveryFee"
                      value={priceForm.deliveryFee}
                      onChange={handlePriceChange}
                      step="0.01"
                      min="0"
                      placeholder="Calculé automatiquement si vide"
                    />
                    <small style={{ color: '#666', fontSize: '12px' }}>
                      Si non renseigné, calculé automatiquement selon le poids total
                    </small>
                  </div>
                </>
              ) : (
                // Ancien format : un seul produit
                <>
                  <div className="form-group">
                    <label htmlFor="productPrice">Prix du produit (USD) *</label>
                    <input
                      type="number"
                      id="productPrice"
                      name="productPrice"
                      value={priceForm.productPrice}
                      onChange={handlePriceChange}
                      step="0.01"
                      min="0"
                      required
                      placeholder="0.00"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="weight">Poids estimé (kg) - Optionnel</label>
                    <input
                      type="number"
                      id="weight"
                      name="weight"
                      value={priceForm.weight}
                      onChange={handlePriceChange}
                      step="0.1"
                      min="0"
                      placeholder="0.0"
                    />
                    <small style={{ color: '#666', fontSize: '12px' }}>
                      Si non renseigné, frais de livraison par défaut: $35
                    </small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="commissionRate">Taux de commission (défaut: 15%)</label>
                    <input
                      type="number"
                      id="commissionRate"
                      name="commissionRate"
                      value={priceForm.commissionRate}
                      onChange={handlePriceChange}
                      step="0.01"
                      min="0"
                      max="1"
                      placeholder="0.15"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="deliveryFee">Frais de livraison (USD) - Optionnel</label>
                    <input
                      type="number"
                      id="deliveryFee"
                      name="deliveryFee"
                      value={priceForm.deliveryFee}
                      onChange={handlePriceChange}
                      step="0.01"
                      min="0"
                      placeholder="Calculé automatiquement si vide"
                    />
                    <small style={{ color: '#666', fontSize: '12px' }}>
                      Si non renseigné, calculé automatiquement selon le poids
                    </small>
                  </div>
                </>
              )}

              {calculation && (
                <div style={{
                  marginTop: '20px',
                  padding: '15px',
                  backgroundColor: '#f0f9ff',
                  borderRadius: '8px',
                  border: '1px solid #bae6fd'
                }}>
                  <h3 style={{ margin: '0 0 10px 0', color: '#0369a1' }}>💰 Calcul automatique:</h3>
                  {calculation.items ? (
                    // Nouveau format : plusieurs produits
                    <div style={{ display: 'grid', gap: '8px' }}>
                      {calculation.items.map((item, index) => (
                        <div key={index} style={{ marginBottom: '10px', padding: '10px', backgroundColor: 'white', borderRadius: '6px' }}>
                          <p style={{ margin: '3px 0', fontSize: '13px', fontWeight: 'bold' }}>
                            Produit {index + 1}:
                          </p>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                            <span>Prix: ${item.productPrice.toFixed(2)}</span>
                            <span>Commission: ${item.commission.toFixed(2)}</span>
                            <span style={{ fontWeight: 'bold' }}>Sous-total: ${item.subtotal.toFixed(2)}</span>
                          </div>
                        </div>
                      ))}
                      <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #bae6fd' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>Prix total produits:</span>
                          <strong>${calculation.totalProductPrice?.toFixed(2) || '0.00'}</strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>Commission totale ({calculation.commissionRate * 100}%):</span>
                          <strong>${calculation.totalCommission?.toFixed(2) || '0.00'}</strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>Frais de livraison:</span>
                          <strong>${calculation.totalDeliveryFee?.toFixed(2) || '0.00'}</strong>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // Ancien format : un seul produit
                    <div style={{ display: 'grid', gap: '8px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Prix produit:</span>
                        <strong>${calculation.productPrice?.toFixed(2) || '0.00'}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Commission ({calculation.commissionRate * 100}%):</span>
                        <strong>${calculation.commission?.toFixed(2) || '0.00'}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Frais de livraison:</span>
                        <strong>${calculation.deliveryFee?.toFixed(2) || '0.00'}</strong>
                      </div>
                    </div>
                  )}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: '10px',
                    paddingTop: '10px',
                    borderTop: '2px solid #bae6fd',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: '#FF0000'
                  }}>
                    <span>Total:</span>
                    <strong>${calculation.totalAmount?.toFixed(2) || '0.00'}</strong>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={calculating || (
                    selectedOrder?.items && Array.isArray(selectedOrder.items) && selectedOrder.items.length > 0
                      ? priceForm.items.some(item => !item.productPrice || parseFloat(item.productPrice) <= 0)
                      : !priceForm.productPrice
                  )}
                >
                  {calculating ? 'Enregistrement...' : 'Enregistrer le prix'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setSelectedOrder(null);
                    setPriceForm({ items: [], productPrice: '', weight: '', commissionRate: '0.15', deliveryFee: '', totalWeight: '' });
                    setCalculation(null);
                  }}
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default AgentDashboard;

